import { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { History, Loader2, RefreshCw } from 'lucide-react';
import { startGmailOAuth } from '@/api/accounts';
import type { Account, AccountConfig, Signature } from '@/types/email';
import { cn } from '@/lib/utils';

export interface AccountFormValues {
  email: string;
  password?: string;
  displayName?: string | null;
  provider: string;
  settings: Record<string, unknown>;
  color?: string | null;
  defaultSignatureId?: string | null;
}

type AccountDialogMode = 'add' | 'edit';

interface AccountDialogProps {
  mode: AccountDialogMode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: AccountFormValues) => Promise<void>;
  isSubmitting: boolean;
  isLoading?: boolean;
  account?: Account | null;
  initialConfig?: AccountConfig | null;
  onFullRefresh?: (() => Promise<void> | void) | null;
  fullRefreshPending?: boolean;
  onFastRecovery?: (() => Promise<void> | void) | null;
  fastRecoveryPending?: boolean;
  signatures: Signature[];
  onManageSignatures?: () => void;
}

const PROVIDERS = [
  { value: 'imap', label: 'Custom IMAP' },
  { value: 'gmail', label: 'Gmail' },
];

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'UTC',
});

function StatusDot({ status }: { status: Account['status'] }) {
  const color =
    status === 'syncing'
      ? 'bg-info'
      : status === 'error'
        ? 'bg-destructive'
        : 'bg-success';
  return <span className={cn('inline-block h-2.5 w-2.5 rounded-full', color)} />;
}

function formatDateTime(value?: string | null): string | null {
  if (!value) {
    return null;
  }
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    const formatted = DATE_TIME_FORMATTER.format(date);
    return `${formatted} UTC`;
  } catch {
    return null;
  }
}

function getStatusLabel(status: Account['status']): string {
  return status === 'error' ? 'Error' : status === 'syncing' ? 'Syncing…' : 'Idle';
}

function sanitizeColorInput(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const text = value.trim();
  if (!text) {
    return null;
  }
  if (!/^#?[0-9a-fA-F]{6}$/.test(text)) {
    return null;
  }
  return text.startsWith('#') ? text : `#${text}`;
}

export function AccountDialog({
  mode,
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
  isLoading = false,
  account,
  initialConfig,
  onFullRefresh,
  fullRefreshPending = false,
  onFastRecovery,
  fastRecoveryPending = false,
  signatures,
  onManageSignatures,
}: AccountDialogProps) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [fromName, setFromName] = useState('');
  const [password, setPassword] = useState('');
  const [provider, setProvider] = useState<string>('imap');
  const [imapHost, setImapHost] = useState('');
  const [imapPort, setImapPort] = useState('993');
  const [imapUsername, setImapUsername] = useState('');
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpUsername, setSmtpUsername] = useState('');
  const [useTls, setUseTls] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [color, setColor] = useState('#4f46e5');
  const [defaultSignatureId, setDefaultSignatureId] = useState<string | null>(null);
  const [gmailAuthState, setGmailAuthState] = useState<string | null>(null);
  const [gmailAuthEmail, setGmailAuthEmail] = useState<string | null>(null);
  const [gmailAuthExpiresAt, setGmailAuthExpiresAt] = useState<string | null>(null);
  const [gmailAuthError, setGmailAuthError] = useState<string | null>(null);
  const [gmailAuthPending, setGmailAuthPending] = useState(false);
  const pendingGmailStateRef = useRef<string | null>(null);
  const gmailPopupRef = useRef<Window | null>(null);
  const SIGNATURE_NONE_VALUE = '__none__';

  useEffect(() => {
    if (!open) {
      return;
    }

    setError(null);
    setGmailAuthError(null);
    setGmailAuthState(null);
    setGmailAuthExpiresAt(null);
    setGmailAuthPending(false);
    pendingGmailStateRef.current = null;

    if (mode === 'add') {
      setEmail('');
      setDisplayName('');
      setFromName('');
      setPassword('');
      setProvider('imap');
      setImapHost('');
      setImapPort('993');
      setImapUsername('');
      setSmtpHost('');
      setSmtpPort('587');
      setSmtpUsername('');
      setUseTls(true);
      setColor('#4f46e5');
      setDefaultSignatureId(null);
      setGmailAuthEmail(null);
      return;
    }

    if (mode === 'edit' && initialConfig) {
      const resolvedProvider = initialConfig.provider || 'imap';
      setProvider(resolvedProvider);
      setColor(initialConfig.color || '#4f46e5');
      setDisplayName(initialConfig.displayName || '');
      setPassword('');

      if (resolvedProvider === 'gmail') {
        const connectedEmail = initialConfig.gmail?.email ?? initialConfig.emailAddress ?? '';
        setEmail(connectedEmail);
        setFromName(initialConfig.gmail?.fromName || '');
        setGmailAuthEmail(initialConfig.gmail?.email ?? null);
        setGmailAuthExpiresAt(initialConfig.gmail?.accessTokenExpiresAt ?? null);
        setImapHost('');
        setImapPort('993');
        setSmtpHost('');
        setSmtpPort('587');
        setUseTls(true);
      } else {
        setEmail(initialConfig.emailAddress || '');
        setFromName(initialConfig.smtp?.fromName || '');
        setGmailAuthEmail(null);
        setGmailAuthExpiresAt(null);

        const imapHostValue = initialConfig.imap?.host ?? '';
        const imapPortValue =
          initialConfig.imap?.port !== null && initialConfig.imap?.port !== undefined
            ? String(initialConfig.imap.port)
            : '993';
        const imapUsernameValue = initialConfig.imap?.username ?? '';
        const smtpHostValue = initialConfig.smtp?.host ?? '';
        const smtpPortValue =
          initialConfig.smtp?.port !== null && initialConfig.smtp?.port !== undefined
            ? String(initialConfig.smtp.port)
            : '587';
        const smtpUsernameValue = initialConfig.smtp?.username ?? '';

        setImapHost(imapHostValue);
        setImapPort(imapPortValue);
        setImapUsername(imapUsernameValue);
        setSmtpHost(smtpHostValue);
        setSmtpPort(smtpPortValue);
        setSmtpUsername(smtpUsernameValue);

        const security = initialConfig.imap?.security
          ? String(initialConfig.imap.security).toLowerCase()
          : null;
        const nextUseTls = security ? ['tls', 'ssl', 'secure', 'starttls'].includes(security) : true;
        setUseTls(nextUseTls);
      }
    }

    if (mode === 'edit') {
      const fallbackDefault = initialConfig?.defaultSignatureId ?? account?.defaultSignatureId ?? null;
      const normalizedDefault = fallbackDefault && signatures.some((signature) => signature.id === fallbackDefault)
        ? fallbackDefault
        : null;
      setDefaultSignatureId(normalizedDefault);
    }
  }, [open, mode, initialConfig, account, signatures]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (provider === 'gmail' && mode === 'add') {
      setColor((current) => sanitizeColorInput(current) ?? '#ea4335');
    }
  }, [provider, open, mode]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!event.data || typeof event.data !== 'object') {
        return;
      }
      const payload = event.data as {
        state?: string;
        status?: string;
        email?: string;
        expiresAt?: string;
        message?: string;
        profile?: unknown;
      };
      if (!payload.state || payload.state !== pendingGmailStateRef.current) {
        return;
      }
      pendingGmailStateRef.current = null;
      if (gmailPopupRef.current && !gmailPopupRef.current.closed) {
        gmailPopupRef.current.close();
      }
      gmailPopupRef.current = null;
      if (payload.status === 'success') {
        const profileData = payload.profile && typeof payload.profile === 'object'
          ? payload.profile as { name?: string }
          : undefined;
        if (profileData?.name) {
          setDisplayName((prev) => prev || profileData.name || '');
        }
        setGmailAuthState(payload.state);
        setGmailAuthEmail(payload.email ?? null);
        setGmailAuthExpiresAt(payload.expiresAt ?? null);
        setGmailAuthError(null);
        if (payload.email) {
          setEmail(payload.email);
        }
      } else {
        setGmailAuthState(null);
        setGmailAuthEmail(null);
        setGmailAuthExpiresAt(null);
        setGmailAuthError(payload.message || 'Gmail authorization failed');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    if (!open && gmailPopupRef.current && !gmailPopupRef.current.closed) {
      gmailPopupRef.current.close();
      gmailPopupRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    if (provider !== 'gmail') {
      setGmailAuthEmail(null);
      setGmailAuthState(null);
      setGmailAuthError(null);
      setGmailAuthExpiresAt(null);
      pendingGmailStateRef.current = null;
    }
  }, [provider]);

  const isGmail = provider === 'gmail';
  const title = mode === 'add' ? 'Add Email Account' : `Edit ${account?.displayName || account?.emailAddress || 'Account'}`;
  const handleGmailConnect = useCallback(async () => {
    if (gmailAuthPending) {
      return;
    }
    try {
      setGmailAuthError(null);
      setGmailAuthPending(true);
      const response = await startGmailOAuth({
        accountId: mode === 'edit' ? account?.id : undefined,
        mode: mode === 'edit' ? 'edit' : 'create',
        loginHint: email || account?.emailAddress || undefined,
      });
      if (!response.url || !response.state) {
        pendingGmailStateRef.current = null;
        setGmailAuthError('Failed to start Gmail authorization.');
        return;
      }
      pendingGmailStateRef.current = response.state;
      const popup = window.open(response.url, 'gmail-oauth', 'width=520,height=720');
      if (!popup) {
        pendingGmailStateRef.current = null;
        setGmailAuthError('Popup blocked. Please allow popups and try again.');
        return;
      }
      gmailPopupRef.current = popup;
    } catch (err) {
      pendingGmailStateRef.current = null;
      setGmailAuthError(err instanceof Error ? err.message : 'Failed to start Gmail authorization');
    } finally {
      setGmailAuthPending(false);
    }
  }, [mode, account?.id, account?.emailAddress, email, gmailAuthPending]);

  const handleSubmit = async () => {
    const isGmailAccount = provider === 'gmail';
    let normalizedEmail = email.trim();

    if (isGmailAccount) {
      const connectedEmail = (gmailAuthEmail ?? initialConfig?.gmail?.email ?? normalizedEmail).trim();
      if (!connectedEmail) {
        setError('Connect your Gmail account before saving.');
        return;
      }
      if (mode === 'add' && !gmailAuthState) {
        setError('Authorize Gmail access before saving.');
        return;
      }
      normalizedEmail = connectedEmail;
    } else {
      if (!normalizedEmail) {
        setError('Email is required');
        return;
      }
      if (mode === 'add' && !password.trim()) {
        setError('Password is required');
        return;
      }
    }

    normalizedEmail = normalizedEmail.toLowerCase();

    const trimmedDisplayName = displayName.trim();
    const trimmedFromName = fromName.trim();
    const normalizedColor = sanitizeColorInput(color);
    const normalizedSignatureId = defaultSignatureId && signatures.some((signature) => signature.id === defaultSignatureId)
      ? defaultSignatureId
      : null;

    const nextSettings: Record<string, unknown> = {};

    if (isGmailAccount) {
      if (gmailAuthState) {
        nextSettings.gmail_auth_state = gmailAuthState;
      }
      if (trimmedFromName) {
        nextSettings.smtp_from_name = trimmedFromName;
      }
    } else {
      let parsedImapPort: number | undefined;
      let parsedSmtpPort: number | undefined;

      const trimmedImapHost = imapHost.trim();
      if (mode === 'add' && !trimmedImapHost) {
        setError('IMAP host is required');
        return;
      }
      if (trimmedImapHost) {
        nextSettings.imap_host = trimmedImapHost;
      }

      const trimmedImapPort = imapPort.trim();
      if (trimmedImapPort) {
        parsedImapPort = Number(trimmedImapPort);
        if (!Number.isFinite(parsedImapPort) || parsedImapPort <= 0) {
          setError('IMAP port must be a positive number');
          return;
        }
        nextSettings.imap_port = parsedImapPort;
      } else if (mode === 'add') {
        setError('IMAP port is required');
        return;
      }

      const trimmedImapUsername = imapUsername.trim();
      if (trimmedImapUsername) {
        nextSettings.imap_username = trimmedImapUsername;
      }

      const trimmedSmtpHost = smtpHost.trim();
      if (trimmedSmtpHost) {
        nextSettings.smtp_host = trimmedSmtpHost;
      }

      const trimmedSmtpPort = smtpPort.trim();
      if (trimmedSmtpPort) {
        parsedSmtpPort = Number(trimmedSmtpPort);
        if (!Number.isFinite(parsedSmtpPort) || parsedSmtpPort <= 0) {
          setError('SMTP port must be a positive number');
          return;
        }
        nextSettings.smtp_port = parsedSmtpPort;
      }

      const trimmedSmtpUsername = smtpUsername.trim();
      if (trimmedSmtpUsername) {
        nextSettings.smtp_username = trimmedSmtpUsername;
      }

      if (trimmedFromName) {
        nextSettings.smtp_from_name = trimmedFromName;
      }

      nextSettings.use_tls = useTls;
    }

    try {
      await onSubmit({
        email: normalizedEmail,
        password: isGmailAccount ? undefined : (password.trim() ? password.trim() : undefined),
        displayName: trimmedDisplayName || null,
        provider,
        settings: nextSettings,
        color: normalizedColor,
        defaultSignatureId: normalizedSignatureId,
      });
    } catch (submissionError: unknown) {
      if (submissionError instanceof Error) {
        setError(submissionError.message || 'Failed to save account');
      } else {
        setError('Failed to save account');
      }
    }
  };

  const isDisabled = isSubmitting || isLoading;
  const showFastRecovery = mode === 'edit' && account && account.provider === 'gmail' && onFastRecovery;
  const showFullRefresh = Boolean(onFullRefresh);
  const actionPending = fullRefreshPending || fastRecoveryPending;

  return (
    <Dialog open={open} onOpenChange={(value) => {
      if (!value) {
        setPassword('');
        setError(null);
      }
      onOpenChange(value);
    }}
    >
      <DialogContent className="w-full max-w-3xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {mode === 'edit' && account && (
          <div className="rounded-xl border border-border/60 bg-background/60 p-4 backdrop-blur">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <StatusDot status={account.status} />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold">{getStatusLabel(account.status)}</span>
                    {account.lastSyncedAt && (
                      <span className="text-xs text-muted-foreground">
                        Last synced {formatDateTime(account.lastSyncedAt)}
                      </span>
                    )}
                  </div>
                  {account.errorMessage && (
                    <p className="text-xs text-destructive mt-1">{account.errorMessage}</p>
                  )}
                </div>
              </div>
              {(showFastRecovery || showFullRefresh) && (
                <div className="flex shrink-0 items-center gap-2">
                  {showFastRecovery && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => onFastRecovery?.()}
                      disabled={isSubmitting || actionPending}
                    >
                      {fastRecoveryPending ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Recovering…
                        </>
                      ) : (
                        <>
                          <History className="h-4 w-4" />
                          Fast recovery
                        </>
                      )}
                    </Button>
                  )}
                  {showFullRefresh && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => onFullRefresh?.()}
                      disabled={isSubmitting || actionPending}
                    >
                      {fullRefreshPending ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Refreshing…
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4" />
                          Full refresh
                        </>
                      )}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="space-y-4 py-2">
          {mode === 'edit' && isLoading && (
            <div className="rounded-md border border-muted/40 bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
              Loading account settings…
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="account-email">Email address</Label>
            <Input
              id="account-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              required
              disabled={isDisabled || isGmail}
            />
            {isGmail && (
              <p className="text-xs text-muted-foreground">
                {gmailAuthEmail || initialConfig?.gmail?.email
                  ? 'Email address is managed through Gmail OAuth.'
                  : 'Connect Gmail to populate your email address.'}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="account-display-name">Display name</Label>
            <Input
              id="account-display-name"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="Optional"
              disabled={isDisabled}
            />
            <p className="text-xs text-muted-foreground">
              This is how the account appears in the UI
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="account-from-name">From name (for sending mail)</Label>
            <Input
              id="account-from-name"
              value={fromName}
              onChange={(event) => setFromName(event.target.value)}
              placeholder="Optional (e.g., Your Name)"
              disabled={isDisabled}
            />
            <p className="text-xs text-muted-foreground">
              This name will appear in the "From" field when you send emails
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="account-color">Account color</Label>
            <div className="flex items-center gap-3">
              <Input
                id="account-color"
                type="color"
                value={sanitizeColorInput(color) || '#4f46e5'}
                onChange={(event) => setColor(event.target.value)}
                className="h-10 w-16 p-1"
                disabled={isDisabled}
              />
              <Input
                type="text"
                value={color}
                onChange={(event) => setColor(event.target.value)}
                placeholder="#4f46e5"
                disabled={isDisabled}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="account-signature">Default signature</Label>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <Select
                value={defaultSignatureId ?? SIGNATURE_NONE_VALUE}
                onValueChange={(value) => {
                  if (value === SIGNATURE_NONE_VALUE) {
                    setDefaultSignatureId(null);
                  } else {
                    setDefaultSignatureId(value);
                  }
                }}
                disabled={isDisabled || signatures.length === 0}
              >
                <SelectTrigger id="account-signature" className="sm:w-72">
                  <SelectValue placeholder={signatures.length ? 'Select signature' : 'No signatures available'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SIGNATURE_NONE_VALUE}>None</SelectItem>
                  {signatures.map((signature) => (
                    <SelectItem key={signature.id} value={signature.id}>
                      {signature.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {onManageSignatures && (
                <Button type="button" variant="outline" size="sm" onClick={onManageSignatures} className="self-start">
                  Manage signatures
                </Button>
              )}
            </div>
          </div>

          {!isGmail && (
            <div className="space-y-2">
              <Label htmlFor="account-password">Password / App Password</Label>
              <Input
                id="account-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder={mode === 'edit' ? 'Leave blank to keep current password' : 'Enter password'}
                disabled={isDisabled}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>Provider</Label>
            <Select
              value={provider}
              onValueChange={(value) => setProvider(value)}
              disabled={isDisabled}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select provider" />
              </SelectTrigger>
              <SelectContent>
                {PROVIDERS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isGmail ? (
            <div className="space-y-3 rounded-md border border-muted/60 bg-muted/20 px-3 py-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium">
                    {gmailAuthEmail || initialConfig?.gmail?.email
                      ? `Connected as ${gmailAuthEmail ?? initialConfig?.gmail?.email}`
                      : 'Connect your Gmail account'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Gmail accounts use the Gmail API. No password or server settings required.
                  </p>
                  {gmailAuthError && (
                    <p className="text-xs text-destructive">{gmailAuthError}</p>
                  )}
                  {gmailAuthExpiresAt && (
                    <p className="text-xs text-muted-foreground">
                      Token expires {new Date(gmailAuthExpiresAt).toLocaleString()}
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  onClick={handleGmailConnect}
                  disabled={isDisabled || gmailAuthPending}
                  variant={gmailAuthEmail || initialConfig?.gmail?.email ? 'outline' : 'default'}
                  className="sm:self-start"
                >
                  {gmailAuthPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {gmailAuthEmail || initialConfig?.gmail?.email ? 'Reconnect Gmail' : 'Connect Gmail'}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="imap-host">IMAP Host</Label>
                  <Input
                    id="imap-host"
                    value={imapHost}
                    onChange={(event) => setImapHost(event.target.value)}
                    placeholder="imap.example.com"
                    disabled={isDisabled}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="imap-port">IMAP Port</Label>
                  <Input
                    id="imap-port"
                    value={imapPort}
                    onChange={(event) => setImapPort(event.target.value)}
                    placeholder="993"
                    disabled={isDisabled}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="imap-username">IMAP Username</Label>
                  <Input
                    id="imap-username"
                    value={imapUsername}
                    onChange={(event) => setImapUsername(event.target.value)}
                    placeholder="Optional (defaults to email)"
                    disabled={isDisabled}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="smtp-host">SMTP Host</Label>
                  <Input
                    id="smtp-host"
                    value={smtpHost}
                    onChange={(event) => setSmtpHost(event.target.value)}
                    placeholder="smtp.example.com"
                    disabled={isDisabled}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="smtp-port">SMTP Port</Label>
                  <Input
                    id="smtp-port"
                    value={smtpPort}
                    onChange={(event) => setSmtpPort(event.target.value)}
                    placeholder="587"
                    disabled={isDisabled}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="smtp-username">SMTP Username</Label>
                  <Input
                    id="smtp-username"
                    value={smtpUsername}
                    onChange={(event) => setSmtpUsername(event.target.value)}
                    placeholder="Optional (defaults to IMAP username)"
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="use-tls"
                  checked={useTls}
                  onCheckedChange={(value) => setUseTls(Boolean(value))}
                  disabled={isDisabled}
                />
                <Label htmlFor="use-tls">Use TLS / SSL</Label>
              </div>
            </>
          )}

          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isDisabled}>
            {isSubmitting ? (mode === 'add' ? 'Adding…' : 'Saving…') : (mode === 'add' ? 'Add Account' : 'Save Changes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
